// =============================================================================
// Project Management module (PROTOTYPE) — lifecycle registry
//
// Self-contained: this whole module is namespaced `pm*` and can be removed by
// deleting the pm* files + the small wiring touches noted in ProjectManagementPipelinePage.tsx.
//
// Each lifecycle is a self-contained, hard-coded definition. Isolation comes from
// one-entry-per-lifecycle in `projectManagementLifecycles` + the engine being lifecycle-agnostic:
// adding a pipeline = add an entry here; no other code changes. A project pins the
// { lifecycleKey, lifecycleVersion } it was created under (see pm.ts).
// =============================================================================

import type { ProjectManagementParameterCategory } from '/imports/api/projectManagement';
import { resTpoV3 } from '/imports/api/projectManagementLifecycleResTpo';
import { resPrepaidV1 } from '/imports/api/projectManagementLifecycleResPrepaid';

// The cross-vertical canonical stages every lifecycle maps onto (for the unified pipeline + reporting).
export type ProjectManagementCanonicalStage =
  | 'origination' | 'qualUwIc' | 'designEng' | 'agreements' | 'permittingIx'
  | 'financingCloseNtp' | 'constructionProcurement' | 'commissioningPtoCod' | 'operation';

export const PROJECT_MANAGEMENT_CANONICAL_STAGES: { key: ProjectManagementCanonicalStage; label: string }[] = [
  { key: 'origination', label: 'Origination / Intake' },
  { key: 'qualUwIc', label: 'Qualification, UW & IC' },
  { key: 'designEng', label: 'Design & Engineering' },
  { key: 'agreements', label: 'Agreements' },
  { key: 'permittingIx', label: 'Permitting & Interconnection' },
  { key: 'financingCloseNtp', label: 'Financing Close & NTP' },
  { key: 'constructionProcurement', label: 'Construction & Procurement' },
  { key: 'commissioningPtoCod', label: 'Commissioning, PTO/COD & Funding' },
  { key: 'operation', label: 'Operation (deferred)' },
];

export interface ProjectManagementStageDef {
  key: string;
  label: string;
  phase: string;
  canonicalStage?: ProjectManagementCanonicalStage; // maps onto the cross-vertical pipeline
  gate?: boolean;        // a decision/approval gate (e.g. IC1)
  terminal?: boolean;
}

export interface ProjectManagementTaskTemplate {
  stage: string;
  key: string;
  label: string;
  // The standing explanation of the task, copied onto every task at creation. Lets `label` stay short
  // enough to scan in a list while the full instruction still travels with it.
  description?: string;
  role?: string;
  requiredDocSlots?: string[];  // docType keys this task delivers (upload from the task; approval can complete it)
  milestone: string;            // key of the schedule-tier milestone this task belongs to (must exist in EVERY tier of the lifecycle)
  // Who does this work by default. 'partner' resolves to the stakeholder whose profile is the project's EPC
  // party, at creation and again whenever the EPC or its memberships change, filling only tasks nobody has
  // been assigned to yet. 'luminia' and absent both leave the task for Luminia to assign by hand.
  owner?: 'partner' | 'luminia';
  // Import-only config, like parameterSeeds' `column`: the column of the tracker's "Estimated Dates and
  // Modeling" sheet that fills this task's estimatedDate (RIC: the hand-entered EPC-schedule dates).
  estimatedDateColumn?: string;
}

export interface ProjectManagementDocSlotDef {
  docType: string;
  label: string;
  stage: string; // stage by which the document is expected
  requirement: 'required' | 'conditional';
  condition?: string;
  // The Documents tab section for a slot no task requires; a task that does require it wins. Absent = the
  // slot lists under "Other Documents" until some task claims it.
  role?: string;
}

// A dated schedule point, instantiated per-project as a Milestone (planned date = project start + offsetDays).
export interface ProjectManagementMilestoneTemplate {
  key: string;
  label: string;
  phase: string;
  canonicalStage: ProjectManagementCanonicalStage;
  ownerRole?: string;
  offsetDays: number;          // planned offset from project start
  isPaymentTrigger?: boolean;  // releases a milestone payment when its required docs are satisfied
  // Opt this milestone into review (Ready for Review → Approved | Deficiency). A reviewed milestone does
  // not complete itself: reaching its requirements only puts it in front of a reviewer. Verticals that
  // declare none — RIC and the CEA pipelines — behave exactly as before.
  reviewed?: boolean;
  requiredDocSlots?: string[]; // docType keys gating the payment
  // When set, this milestone deterministically corresponds to a cash event of this direction. Completing
  // the milestone creates/pays that event, and paying the event completes the milestone (see methods).
  // Legacy single-payment form, still used by the SGIP / CEA lifecycles. RIC uses `payments` below.
  paymentDirection?: 'in' | 'out';
  paymentAmount?: number;      // optional estimated amount for an auto-created event (else left blank)
  // The Sources/Uses this milestone OWNS. Each keeps its own stable payment `code` as the cash event's
  // milestoneKey (m0..m6 / lntp / fntp / wo3 / equipmentWo — what the tracker import, QuickBooks tokens,
  // and the funding-rollup constants match on); the cash event's milestoneId points back at this
  // milestone. A milestone may own several rows and both directions (RIC collapsed 14 payment-milestones
  // into 6), so this replaces the 1:1 paymentDirection for RIC. Seeded by ensurePaymentPlaceholders.
  payments?: { code: string; direction: 'in' | 'out'; label: string; amount?: number }[];
}

// One or more per lifecycle; the per-project tier is chosen by `selector` (e.g. by MW), else the first
// selector-less tier, else the first. (FTM has <3MW and >3MW tiers; RIC has one.)
export interface ProjectManagementScheduleTier {
  key: string;
  label: string;
  selector?: (project: { capacityKwDc?: number; storageKwh?: number }) => boolean;
  milestones: ProjectManagementMilestoneTemplate[];
}

// A vertical's money model. 'ric' is the Luminia development-fee world: dev fee, tenant billing, ancillary
// fees, the EPC M0–M6 draw split and the change-order waterfall. 'resTpo' pays a dealer a share of a build
// cost as each reviewed milestone is approved, and has no Luminia fee at all.
export type ProjectManagementFinanceModel = 'ric' | 'resTpo';

/** One dealer payment: the DEFAULT share of the purchase price that the named milestone's approval
 *  releases. A project overrides it per payment through its cash event's drawShare. */
export interface ProjectManagementDealerPayment {
  milestone: string;   // milestone template key
  code: string;        // the cash event's stable milestoneKey, matching the milestone's `payments`
  pct: number;         // default share of the build-cost parameter (0.7 = 70%)
}

export interface ProjectManagementLifecycleFinance {
  model: ProjectManagementFinanceModel;
  dealerSchedule?: ProjectManagementDealerPayment[];  // resTpo
  buildCostParameter?: string;   // resTpo: the parameter the dealer shares are taken against
  // resTpo: the Uses whose total is this vertical's Fees figure. RIC's fees are what Luminia itself makes
  // and are worked out from the fee parameters (projectManagementTotalFees); a vertical declaring these
  // has its fees sitting in the fund model instead, as money the fund pays out.
  feeUseCodes?: string[];
  // resTpo: the Use whose amount the dealer shares are taken against, and which OUTRANKS the parameter
  // above. The fund model writes this one for every project it prices; the parameter is keyed by hand or
  // by the tracker import, and a project the model priced alone has none — which left its payments blank.
  buildCostUseCode?: string;
  // Where the banner's Sources figure comes from, at both the project and the portfolio level.
  //
  // ABSENT MEANS RIC's: the money-in cash ledger, because RIC's inflows ARE its sources — a source's
  // estimate is drawn down by the work orders booked against it, and summing the sources instead would
  // both change a live figure and double-count an ITC that is carried gross AND net.
  //
  // A vertical whose sources are receipts rather than draws declares 'fundingSources'. Residential's SGIP
  // incentive is paid once on its own schedule: the amount is known from the start and no draw is ever
  // booked, so the ledger is empty and the sources are the only record of the money coming in.
  sourcesFigure?: ProjectManagementLifecycleSourcesFigure;
}

/** Which record a vertical's Sources figure is read from — see `finance.sourcesFigure`. */
export type ProjectManagementLifecycleSourcesFigure = 'cashLedger' | 'fundingSources';

/** Where the project banner's Estimated PTO comes from: the named task's ESTIMATED date, or the named
 *  milestone's PLANNED date. A vertical whose tasks carry no estimated dates has only the second. */
export interface ProjectManagementLifecyclePtoEstimate {
  from: 'task' | 'milestone';
  key: string;   // taskTemplates[].key, or a milestone template's key
}

/**
 * What the Project Details tab leaves out for this vertical.
 *
 * Hiding, never removing: the parameters are still stored, still published, still read on the Tasks tab
 * and still what a task's completion counts. This is only what the tab lists.
 */
export interface ProjectManagementLifecycleProjectDetails {
  /** Categories whose section does not render at all. */
  hiddenCategories?: ProjectManagementParameterCategory[];
  /** Categories that list only their estimate-vs-actual rows, leaving the single-figure ones out. */
  trackedOnlyCategories?: ProjectManagementParameterCategory[];
}

/** Where a vertical's financial / pricing model is uploaded and which deterministic parser reads it. */
export interface ProjectManagementLifecycleFinancialModel {
  docType: string;            // the docSlot the model is uploaded into
  parser: 'bta' | 'prepaidCalculator' | 'prepaidTeCalculator';   // the server/integrations parser that reads it
}

export interface ProjectManagementLifecycle {
  key: string;
  version: number;
  label: string;
  channel: string;
  phases: { key: string; label: string; deferred?: boolean }[];
  stages: ProjectManagementStageDef[]; // ordered; engine relies on sequence for neighbour transitions
  initialStage: string;
  branches?: { [stageKey: string]: string[] }; // extra allowed targets beyond linear neighbours
  taskTemplates: ProjectManagementTaskTemplate[];
  docSlots: ProjectManagementDocSlotDef[];
  scheduleTiers?: ProjectManagementScheduleTier[]; // milestone templates; absent → no milestones instantiated
  // The vertical's parameter catalog: instantiated EMPTY per project, then filled by the tracker import
  // (`column` → the ESTIMATE only; an actual is only ever set by a person) or by hand from the task row. Array order is the display order — both the
  // Project Details tab (within a category) and a task's Details block — so this list IS the tab layout.
  parameterSeeds?: {
    name: string;
    type: 'boolean' | 'string' | 'number' | 'integer' | 'date';
    category?: ProjectManagementParameterCategory;  // Project Details grouping; absent → "Other"
    taskKey?: string;
    unit?: string;
    column?: string;        // source column in the vertical's import workbook (RIC: the portfolio tracker); fills the ESTIMATE
    // Whether an ACTUAL is meaningful, i.e. whether this is a forecast that reality later confirms.
    // ABSENT MEANS ON — set false to opt a parameter out and render it as a single Value instead.
    tracksActual?: boolean;
  }[];
  // --- The vertical's money model ---
  // Shared code (fee helpers, the Budget tab, the pipeline's money columns) reads this instead of assuming
  // RIC's vocabulary, so one vertical's economics live in one vertical's file. ABSENT MEANS 'ric', which is
  // what every lifecycle ran before residential declared its own — so leaving it off changes nothing.
  finance?: ProjectManagementLifecycleFinance;
  // --- The vertical's financial / pricing model ---
  // The slot the Project Details upload-and-parse reads and the parser that reads it. Each parser is its
  // own server module; shared code only dispatches on the key. ABSENT MEANS RIC's: the 'financialModel'
  // slot read by the BTA parser, with the AI fallback behind it — what every lifecycle ran before
  // residential declared its own.
  financialModel?: ProjectManagementLifecycleFinancialModel;
  // --- The banner's Estimated PTO ---
  // ABSENT MEANS RIC's: the 'pto' task's estimated date, which the portfolio tracker fills through that
  // template's estimatedDateColumn. A vertical that imports no task dates has nothing there to read and
  // points at a milestone instead.
  ptoEstimate?: ProjectManagementLifecyclePtoEstimate;
  saleAtNtp?: boolean;             // supports the early-exit "sold at NTP" branch
  changeOrderApprovalPct?: number; // change orders above this % of contract require IC approval
  billingConfigurable?: boolean;   // e.g. the CCA (Calpine) billing path is a configurable option
  roles?: { key: string; label: string; kind: 'internal' | 'external' }[]; // assignable project roles (key matches milestone ownerRole / task role)
  externalIdLabel?: string;        // vertical-specific display label for externalId (e.g. RIC's "RIC #")
  // Shows the review roll-up (awaiting / deficiencies / average approval time) above the pipeline list.
  // Declared rather than derived from whether any milestone is `reviewed`: residential reviews milestones
  // but reports on them inside the project, not across the portfolio. Absent is RIC's behaviour — no
  // roll-up — so a vertical that wants one opts in.
  pipelineReviewSummary?: boolean;
  // --- What the Project Details tab leaves out ---
  // ABSENT MEANS RIC's: the tab lists every parameter the project carries.
  projectDetails?: ProjectManagementLifecycleProjectDetails;
  // Leads every luminiaId minted for this vertical (RIC0001). Required, and asserted unique across the
  // registry below: a duplicate would hand two verticals the same project id.
  idPrefix: string;
}

// -----------------------------------------------------------------------------
// Lifecycle: V1 RIC — C&I rooftop/carport solar + storage for Realty Income (REIT).
// Phases Development → Pre-Construction → Construction → Post-construction (deferred). Money-in =
// WO#1 (LNTP) / WO#2 (FNTP) / WO#3 (substantial/ITC sale) + Equipment-Only; money-out = EPC M0–M6.
// Milestone offsets follow the indicative ~40-week RIC Gantt (adjustable per project). See miscl/PMspecs3/V1_RIC.
// -----------------------------------------------------------------------------
const ricV1: ProjectManagementLifecycle = {
  key: 'ric',
  version: 1,
  label: 'RIC C&I Solar + Storage (Realty Income)',
  idPrefix: 'RIC',
  channel: 'ric',
  saleAtNtp: true,
  changeOrderApprovalPct: 3,
  externalIdLabel: 'RIC #',
  // Array order = Tasks-panel bucket order.
  roles: [
    { key: 'Development', label: 'Development', kind: 'internal' },
    { key: 'Contracts', label: 'Contracts', kind: 'internal' },
    { key: 'Interconnection', label: 'Interconnection', kind: 'internal' },
    { key: 'Engineering', label: 'Engineering', kind: 'external' },
    { key: 'Procurement', label: 'Procurement', kind: 'internal' },
    { key: 'Construction', label: 'Construction', kind: 'internal' },
    { key: 'Investment Committee', label: 'Investment Committee', kind: 'internal' },
    { key: 'Utility', label: 'Utility', kind: 'external' },
  ],
  phases: [
    { key: 'development', label: 'Development' },
    { key: 'preConstruction', label: 'Pre-Construction' },
    { key: 'construction', label: 'Construction' },
    { key: 'postConstruction', label: 'Post-Construction', deferred: true },
  ],
  stages: [
    { key: 'modeling', label: 'Modeling Inputs', phase: 'development', canonicalStage: 'origination' },
    { key: 'economics', label: 'Economics', phase: 'development', canonicalStage: 'qualUwIc' },
    { key: 'agreements', label: 'Agreements (PPA / Framework / EPC)', phase: 'development', canonicalStage: 'agreements' },
    { key: 'siteReview', label: 'Site Review & Roof', phase: 'development', canonicalStage: 'designEng' },
    { key: 'safeHarbor', label: 'Safe Harbor', phase: 'development', canonicalStage: 'designEng' },
    { key: 'permittingIx', label: 'Permitting & Interconnection', phase: 'preConstruction', canonicalStage: 'permittingIx', gate: true },
    { key: 'financialClose', label: 'Financial Close', phase: 'preConstruction', canonicalStage: 'financingCloseNtp', gate: true },
    { key: 'lntp', label: 'LNTP', phase: 'construction', canonicalStage: 'financingCloseNtp' },
    { key: 'construction', label: 'Construction (M0–M6)', phase: 'construction', canonicalStage: 'constructionProcurement' },
    { key: 'substantialPto', label: 'Substantial Completion & PTO', phase: 'construction', canonicalStage: 'commissioningPtoCod' },
    { key: 'itcSale', label: 'ITC Sale (deferred)', phase: 'postConstruction', canonicalStage: 'operation' },
    { key: 'closed', label: 'Closed / Funded', phase: 'postConstruction', canonicalStage: 'operation', terminal: true },
    { key: 'cancelled', label: 'Cancelled', phase: 'postConstruction', canonicalStage: 'operation', terminal: true },
  ],
  initialStage: 'modeling',
  taskTemplates: [
    { stage: 'modeling', key: 'consumptionData', label: 'Consumption Data', role: 'Development', requiredDocSlots: ['consumptionData'], milestone: 'ppaExecuted' },
    { stage: 'modeling', key: 'helioscope', label: 'Preliminary Helioscope', role: 'Development', requiredDocSlots: ['helioscope'], milestone: 'ppaExecuted' },
    { stage: 'modeling', key: 'etb', label: 'ETB', role: 'Development', requiredDocSlots: ['etbProposal'], milestone: 'ppaExecuted' },
    { stage: 'modeling', key: 'finalHelioscope', label: 'Final Helioscope', role: 'Development', requiredDocSlots: ['finalHelioscope'], milestone: 'ppaExecuted' },
    // The Financial Model task is retired: the model is uploaded and parsed straight from Project
    // Details, so a task whose only job was to carry the doc slot had nothing left to track. The
    // 'financialModel' docSlot below deliberately stays — doc slots do not need a task to exist.
    { stage: 'agreements', key: 'ppaLease', label: 'PPA / Lease', role: 'Contracts', requiredDocSlots: ['ppa'], milestone: 'ppaExecuted' },
    { stage: 'agreements', key: 'epcContract', label: 'EPC Contract', role: 'Contracts', requiredDocSlots: ['epcContract'], milestone: 'lntp' },
    { stage: 'siteReview', key: 'siteSurvey', label: 'Site Survey', role: 'Development', requiredDocSlots: ['preConstruction', 'siteSurvey'], milestone: 'lntp', estimatedDateColumn: 'X' },
    { stage: 'siteReview', key: 'roofSurvey', label: 'Roof Survey', role: 'Development', requiredDocSlots: ['roofSurvey'], milestone: 'lntp' },
    { stage: 'modeling', key: 'projectRfp', label: 'Project RFP', role: 'Development', requiredDocSlots: ['luminiaRfp'], milestone: 'lntp' },
    { stage: 'lntp', key: 'lntpSent', label: 'LNTP Sent', role: 'Development', milestone: 'lntp' },
    { stage: 'safeHarbor', key: 'specSheets', label: 'Spec Sheets and Drawings', role: 'Procurement', requiredDocSlots: ['specModules', 'specInverters', 'specRacking', 'specElectrical', 'specBess', 'specDas'], milestone: 'fntp' },
    { stage: 'safeHarbor', key: 'serialNumbers', label: 'Serial Numbers', role: 'Procurement', requiredDocSlots: ['modules', 'inverters', 'das', 'misclEquipment'], milestone: 'mechanical' },
    // IX + Engineering task split per Janaswamy 8-05 (replaces the monolithic Permitting/Interconnection task)
    { stage: 'permittingIx', key: 'ixPreApplication', label: 'IX Pre-Application', role: 'Interconnection', requiredDocSlots: ['ixPreApplication'], milestone: 'fntp' },
    { stage: 'permittingIx', key: 'ixApplication', label: 'IX Application', role: 'Interconnection', requiredDocSlots: ['ixApplication'], milestone: 'fntp', estimatedDateColumn: 'Z' },
    { stage: 'permittingIx', key: 'design30', label: '30% Design', role: 'Interconnection', requiredDocSlots: ['design30'], milestone: 'fntp', estimatedDateColumn: 'Y' },
    { stage: 'permittingIx', key: 'ixAgreement', label: 'IX Agreement', role: 'Interconnection', requiredDocSlots: ['ixAgreement'], milestone: 'substantial', estimatedDateColumn: 'AA' },
    { stage: 'permittingIx', key: 'ifpPlanSet', label: 'IFP Plan Set', role: 'Engineering', requiredDocSlots: ['ifpPlanSet'], milestone: 'fntp', estimatedDateColumn: 'AB' },
    { stage: 'permittingIx', key: 'ifcPlanSet', label: 'IFC Plan Set', role: 'Engineering', requiredDocSlots: ['ifcPlanSet'], milestone: 'fntp' },
    { stage: 'permittingIx', key: 'permitIssued', label: 'Permit', role: 'Engineering', requiredDocSlots: ['permit'], milestone: 'fntp', estimatedDateColumn: 'AC' },
    { stage: 'construction', key: 'fntp', label: 'FNTP', role: 'Construction', requiredDocSlots: ['fntpLienWaiver'], milestone: 'fntp', estimatedDateColumn: 'AF' },
    // Construction photo package (2026-08-24): the EPC's site-evidence shots. Deliberately carry no
    // doc slots or details — the photos themselves are attached ad hoc, so each is a plain checkbox.
    // All gate the Mechanical milestone (which owns the M3-Electrical and M4-Mechanical Uses); the
    // electrical-evidence vs mechanical-evidence distinction now lives only on those two cash codes.
    { stage: 'construction', key: 'acEquipmentTerminationPhotos', label: 'AC Equipment Termination Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'acHomerunPhotos', label: 'AC Homerun Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'dasPhotos', label: 'DAS Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'dcAcInverterTerminationPhotos', label: 'DC and AC Inverter Termination Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'dcHomerunPhotos', label: 'DC Homerun Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'dcStringingPhotos', label: 'DC Stringing Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'poiAttachmentMethodPhotos', label: 'POI Attachment Method Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'pvArrayPhotos', label: 'PV Array Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'sitePhotos', label: 'Site Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'inverterLocationStickerPhotos', label: 'Inverter Location and Sticker Photos', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'pvEquipmentStickerPhotos', label: 'Sticker photos of all PV Equipment', role: 'Construction', milestone: 'mechanical' },
    { stage: 'construction', key: 'mechanicalElectrical', label: 'Mechanical/Electrical', role: 'Construction', requiredDocSlots: ['mechanicalLienWaiver', 'mechanicalCompletionCert'], milestone: 'mechanical', estimatedDateColumn: 'AG' },
    { stage: 'substantialPto', key: 'inspection', label: 'Substantial Completion', role: 'Construction', requiredDocSlots: ['ahjInspection', 'utilityInspection', 'substantialLienWaiver', 'substantialCompletionCert'], milestone: 'substantial', estimatedDateColumn: 'AI' },
    { stage: 'substantialPto', key: 'pto', label: 'PTO', role: 'Interconnection', requiredDocSlots: ['ptoLetter'], milestone: 'substantial', estimatedDateColumn: 'AI' },
    { stage: 'construction', key: 'completion', label: 'Final Completion', role: 'Construction', requiredDocSlots: ['asBuilts', 'capacityTest', 'commissioning', 'qaQc', 'warranties', 'closeOutManual', 'monitoringVerified', 'omCredentials', 'punchList', 'lienWaiver', 'finalCompletionCert'], milestone: 'final', estimatedDateColumn: 'AJ' },
  ],
  // Full system-parameter catalog (generated from the importer's upsertParameter calls — names must
  // match exactly so a later tracker import FILLS these rather than duplicating them).
  // The RIC parameter catalog — the "Project Details - revised params 2026-08-20" spec, verbatim: one
  // entry per row of that sheet, in its order, grouped by its column B. `column` is the portfolio tracker's
  // source column for the ESTIMATE — imports never write actuals (2026-09-17: the tracker's "final" columns
  // N / AC / AQ used to land as actuals; an actual now only comes from a person). Entries without a column
  // are PM-entered.
  parameterSeeds: [
    // PROJECT DETAILS
    // Removed 2026-09-09: 'Detailed Build Cost on EPCA' (AP) duplicated System Build Cost's actual;
    // 'Consumption Data Received' (Y) and 'Is 15-min intervals' are already captured on the Consumption
    // Data task. Column AP still feeds System Build Cost's actual below.
    { name: 'PV System Size', type: 'number', unit: 'kW DC', category: 'projectDetails' },  // model: BTA Model "Solar Project Size (kWdc)" (B16 → C16), not the tracker
    { name: 'PV Estimated Production', type: 'number', unit: 'kWh', category: 'projectDetails', column: 'L' },
    { name: 'BESS Size (kW)', type: 'number', unit: 'kW', category: 'projectDetails' },      // model: "Storage (ESS) Output (kW)" (B19 → C19)
    { name: 'BESS Capacity (kWh)', type: 'number', unit: 'kWh', category: 'projectDetails' }, // model: "Storage (ESS) Capacity (kWh)" (B18 → C18)
    { name: 'System Build Cost', type: 'number', unit: '$', category: 'projectDetails', column: 'AA' },
    // The EPC's $/W, under System Build Cost and linked to the EPC Contract task. Model: BTA Model "Solar
    // Build Cost ($/W)"; a person confirms the actual from the executed contract. (Replaced 'EPCA Cost per W'
    // 2026-09-17 — same figure, and the fee formulas already ran on this name.)
    { name: 'Solar Build Cost per W', type: 'number', unit: '$/W', category: 'projectDetails', taskKey: 'epcContract' },
    // AGREEMENTS
    { name: 'PPA Executed', type: 'date', category: 'agreements', taskKey: 'ppaLease', column: 'AJ', tracksActual: false },
    { name: 'PPA Rate / Lease Payment', type: 'number', category: 'agreements', taskKey: 'ppaLease', tracksActual: false },
    { name: 'PPA Term', type: 'integer', unit: 'years', category: 'agreements', taskKey: 'ppaLease', tracksActual: false },
    { name: 'PPA Escalator', type: 'number', unit: '%', category: 'agreements', taskKey: 'ppaLease', tracksActual: false },
    // SITE REVIEW & EPC
    { name: 'Site Survey', type: 'date', category: 'siteReview', taskKey: 'siteSurvey', column: 'AK', tracksActual: false },
    { name: 'Roof Survey', type: 'date', category: 'siteReview', taskKey: 'roofSurvey', column: 'AN', tracksActual: false },
    { name: 'Reroof Needed', type: 'boolean', category: 'siteReview', taskKey: 'roofSurvey', column: 'AL', tracksActual: false },
    { name: 'Roof Contractor', type: 'string', category: 'siteReview', taskKey: 'roofSurvey', column: 'AM', tracksActual: false },
    { name: 'Reroof Start', type: 'date', category: 'siteReview', taskKey: 'roofSurvey', column: 'AO', tracksActual: false },
    { name: 'Reroof Completion', type: 'date', category: 'siteReview', taskKey: 'roofSurvey', column: 'AP', tracksActual: false },
    // 'EPC' parameter removed 2026-09-08 — the EPC name lives on the funding/payee stakeholder (from the
    // same tracker column S), so echoing it as a Project Details parameter was redundant.
    { name: 'EPC Contract Executed', type: 'date', category: 'siteReview', taskKey: 'epcContract', column: 'AS', tracksActual: false },
    // PERMITTING & INTERCONNECTION
    { name: '30% Design Complete', type: 'date', category: 'permitting', taskKey: 'design30', column: 'AT', tracksActual: false },
    { name: 'Permit App/IFP Complete', type: 'date', category: 'permitting', taskKey: 'ifpPlanSet', column: 'AU', tracksActual: false },
    { name: 'Permit Approved', type: 'date', category: 'permitting', taskKey: 'permitIssued', column: 'AV', tracksActual: false },
    { name: 'IX App Submitted', type: 'date', category: 'permitting', taskKey: 'ixApplication', column: 'AX', tracksActual: false },
    { name: 'Utility Inspection Status', type: 'string', category: 'permitting', taskKey: 'inspection', column: 'DM', tracksActual: false },
    { name: 'PTO Received', type: 'date', category: 'permitting', taskKey: 'pto', column: 'DN', tracksActual: false },
    // EQUIPMENT
    { name: 'Module Manufacturer', type: 'string', category: 'equipment', taskKey: 'specSheets', column: 'BA', tracksActual: false },
    { name: 'Module Model Number', type: 'string', category: 'equipment', taskKey: 'specSheets', column: 'BB', tracksActual: false },
    { name: 'Module Wattage Class', type: 'number', unit: 'W', category: 'equipment', taskKey: 'specSheets', column: 'BC', tracksActual: false },
    { name: 'Number of Modules', type: 'number', category: 'equipment', taskKey: 'serialNumbers', column: 'BD', tracksActual: false },
    { name: 'Inverter Manufacturer', type: 'string', category: 'equipment', taskKey: 'specSheets', column: 'BE', tracksActual: false },
    { name: 'Inverter Model Number', type: 'string', category: 'equipment', taskKey: 'specSheets', column: 'BF', tracksActual: false },
    { name: 'Inverter Capacity', type: 'number', category: 'equipment', taskKey: 'specSheets', column: 'BG', tracksActual: false },
    { name: 'Number of Inverters', type: 'integer', category: 'equipment', taskKey: 'serialNumbers', column: 'BH', tracksActual: false },
    { name: 'Racking Option', type: 'string', category: 'equipment', taskKey: 'specSheets', column: 'BI', tracksActual: false },
    // 'Warranties' parameter removed 2026-09-08 — the warranties live on the 'warranties' document slot
    // (on the Final Completion task), so a blank PM-entered parameter echoing it added nothing.
    // The three Equipment cost parameters (Module Cost BM, Design & Admin Fee BN, Only-WO Total Cost BO)
    // were removed 2026-09-08, joining the work-order/EPC amounts below.
    // Work-order and EPC-payment amounts/dates are DELIBERATELY not parameters (removed 2026-08-28):
    // they duplicated the Budget ledger, which is their system of record. The importer still reads the
    // same tracker columns (BH/BT/BX, CB/CE/..., and BO for the Equipment-Only WO) straight into cash
    // events — see upsertCash.
  ],
  docSlots: [
    { docType: 'luminiaRfp', label: 'Luminia RFP', stage: 'modeling', requirement: 'required' },
    { docType: 'consumptionData', label: 'Consumption Data', stage: 'modeling', requirement: 'required' },
    { docType: 'etbProposal', label: 'ETB proposal', stage: 'modeling', requirement: 'conditional', condition: 'Optional — ETB output file only exists when exported' },
    { docType: 'financialModel', label: 'Financial Model (.xlsx)', stage: 'economics', requirement: 'required', role: 'Development' },
    { docType: 'ppa', label: 'Executed PPA / Framework Agreement', stage: 'agreements', requirement: 'required' },
    { docType: 'epcContract', label: 'Executed EPC Contract', stage: 'agreements', requirement: 'required' },
    { docType: 'helioscope', label: 'Preliminary Helioscope Design', stage: 'modeling', requirement: 'required' },
    { docType: 'finalHelioscope', label: 'Final Helioscope Design', stage: 'modeling', requirement: 'required' },
    { docType: 'preConstruction', label: 'Pre-Construction', stage: 'siteReview', requirement: 'required' },
    { docType: 'siteSurvey', label: 'Site Survey', stage: 'siteReview', requirement: 'required' },
    { docType: 'roofSurvey', label: 'Roof Survey', stage: 'siteReview', requirement: 'required' },
    { docType: 'ixPreApplication', label: 'Interconnection Pre-Application', stage: 'permittingIx', requirement: 'conditional', condition: 'Project-dependent — congestion screen before the IX application' },
    { docType: 'ixApplication', label: 'Interconnection Application', stage: 'permittingIx', requirement: 'required' },
    { docType: 'design30', label: '30% Design (Site Plan + Single-Line Diagram)', stage: 'permittingIx', requirement: 'required' },
    { docType: 'permit', label: 'Building Permit', stage: 'permittingIx', requirement: 'required' },
    { docType: 'ixAgreement', label: 'Executed Interconnection Agreement', stage: 'permittingIx', requirement: 'required' },
    { docType: 'specModules', label: 'Modules', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'specInverters', label: 'Inverters', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'specRacking', label: 'Racking', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'specElectrical', label: 'Electrical Equipment', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'specBess', label: 'BESS', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'specDas', label: 'DAS', stage: 'safeHarbor', requirement: 'required' },
    // Serial numbers are collected per equipment class rather than as one combined document.
    { docType: 'modules', label: 'Modules', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'inverters', label: 'Inverters', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'das', label: 'DAS', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'misclEquipment', label: 'Miscl. Equipment', stage: 'safeHarbor', requirement: 'required' },
    { docType: 'ifpPlanSet', label: 'IFP Plan Set', stage: 'permittingIx', requirement: 'required' },
    { docType: 'ifcPlanSet', label: 'IFC Plan Set', stage: 'permittingIx', requirement: 'required' },
    { docType: 'asBuilts', label: 'As-Builts', stage: 'construction', requirement: 'required' },
    // Retired 2026-11-02: no task delivered it and not one of the 258 prod copies ever held a file. Uncomment to
    // restore, then put the existing rows back with miscl/scripts/retireRicInvoicesDocSlot.js RESTORE=1.
    // { docType: 'invoices', label: 'EPC Invoices & Lien Waivers', stage: 'construction', requirement: 'required' },
    { docType: 'ahjInspection', label: 'AHJ Inspection', stage: 'substantialPto', requirement: 'required' },
    { docType: 'utilityInspection', label: 'Utility Inspection', stage: 'substantialPto', requirement: 'required' },
    { docType: 'substantialCompletionCert', label: 'Substantial Completion Certificate', stage: 'substantialPto', requirement: 'required' },
    // Its own slot, not the Final Completion waiver: the two milestones collect separate waivers.
    { docType: 'substantialLienWaiver', label: 'Lien Waiver', stage: 'substantialPto', requirement: 'required' },
    { docType: 'ptoLetter', label: 'PTO Letter', stage: 'substantialPto', requirement: 'required' },
    // Own slots, not the Substantial/Final ones: each milestone collects its own waiver and certificate.
    { docType: 'fntpLienWaiver', label: 'Lien Waiver', stage: 'construction', requirement: 'required' },
    { docType: 'mechanicalLienWaiver', label: 'Lien Waiver', stage: 'construction', requirement: 'required' },
    { docType: 'mechanicalCompletionCert', label: 'Completion Certificate', stage: 'construction', requirement: 'required' },
    { docType: 'capacityTest', label: 'Capacity Test', stage: 'construction', requirement: 'required' },
    { docType: 'commissioning', label: 'Commissioning', stage: 'construction', requirement: 'required' },
    { docType: 'qaQc', label: 'QA/QC Complete', stage: 'construction', requirement: 'required' },
    { docType: 'warranties', label: 'Warranties', stage: 'construction', requirement: 'required' },
    { docType: 'closeOutManual', label: 'Close Out Manual (O&M)', stage: 'construction', requirement: 'required' },
    { docType: 'monitoringVerified', label: 'Monitoring Verified', stage: 'construction', requirement: 'required' },
    { docType: 'omCredentials', label: 'O&M Credentials', stage: 'construction', requirement: 'required' },
    { docType: 'punchList', label: 'Punch List Complete', stage: 'construction', requirement: 'required' },
    { docType: 'finalCompletionCert', label: 'Final Completion Certificate', stage: 'construction', requirement: 'required' },
    { docType: 'lienWaiver', label: 'Lien Waiver', stage: 'construction', requirement: 'required' },
  ],
  scheduleTiers: [
    {
      key: 'standard',
      label: 'Standard C&I (~40 weeks)',
      // Six milestones (reduced from 14 on 2026-09-08). The former payment-milestones (m0–m6, the WO
      // draws) are no longer milestones — they live on as cash-event payment CODES, grouped under these
      // six via each cash event's milestoneId. A milestone completes when all its tasks are done AND all
      // its owned cash events are paid (see evaluateMilestoneCompletion). Offsets are indicative.
      milestones: [
        { key: 'ppaExecuted', label: 'PPA', phase: 'development', canonicalStage: 'agreements', ownerRole: 'Contracts', offsetDays: 91 },
        { key: 'lntp', label: 'LNTP', phase: 'construction', canonicalStage: 'financingCloseNtp', ownerRole: 'Development', offsetDays: 98, requiredDocSlots: ['ppa'], payments: [
          { code: 'lntp', direction: 'in', label: 'WO #1 – LNTP' },
          { code: 'equipmentWo', direction: 'in', label: 'Equipment Only WO' },
          { code: 'm0', direction: 'out', label: 'M0 – LNTP' },
        ] },
        { key: 'fntp', label: 'FNTP', phase: 'construction', canonicalStage: 'constructionProcurement', ownerRole: 'Development', offsetDays: 119, requiredDocSlots: ['permit'], payments: [
          { code: 'fntp', direction: 'in', label: 'WO #2 – FNTP' },
          { code: 'm1', direction: 'out', label: 'M1 – FNTP/Permit' },
        ] },
        { key: 'mechanical', label: 'Mechanical', phase: 'construction', canonicalStage: 'constructionProcurement', ownerRole: 'Construction', offsetDays: 168, payments: [
          { code: 'm2', direction: 'out', label: 'M2 – Mobilization' },
          { code: 'm3', direction: 'out', label: 'M3 – Electrical' },
          { code: 'm4', direction: 'out', label: 'M4 – Mechanical' },
        ] },
        { key: 'substantial', label: 'Substantial', phase: 'construction', canonicalStage: 'commissioningPtoCod', ownerRole: 'Construction', offsetDays: 182, payments: [
          { code: 'wo3', direction: 'in', label: 'WO #3 – Substantial' },
          { code: 'm5', direction: 'out', label: 'M5 – Substantial' },
        ] },
        { key: 'final', label: 'Final', phase: 'construction', canonicalStage: 'commissioningPtoCod', ownerRole: 'Construction', offsetDays: 210, payments: [
          { code: 'm6', direction: 'out', label: 'M6 – Final' },
        ] },
      ],
    },
  ],
};

// -----------------------------------------------------------------------------
// Lifecycle: V2 CEA-BTM — commercial behind-the-meter solar + storage via a CCA (Clean Energy
// Alliance) on a PPA/ESSA. Four-phase SOP with the IC1 gate; Rule 21 interconnection; Calpine
// billing kept configurable; supports the "sold at NTP" early exit. See miscl/PMspecs3/V2_C&I - CCA.
// -----------------------------------------------------------------------------
const ceaBtmV1: ProjectManagementLifecycle = {
  key: 'cea-btm',
  version: 1,
  label: 'CEA Commercial BTM Solar + Storage',
  idPrefix: 'BTM',
  channel: 'cea-btm',
  saleAtNtp: true,
  billingConfigurable: true,
  changeOrderApprovalPct: 3,
  roles: [
    { key: 'Sales', label: 'Sales', kind: 'internal' },
    { key: 'Project Development', label: 'Project Development', kind: 'internal' },
    { key: 'Engineering', label: 'Engineering', kind: 'internal' },
    { key: 'Legal', label: 'Legal', kind: 'internal' },
    { key: 'Capital Markets', label: 'Capital Markets', kind: 'internal' },
    { key: 'Investment Committee', label: 'Investment Committee', kind: 'internal' },
    { key: 'EPC', label: 'EPC', kind: 'external' },
    { key: 'Utility', label: 'Utility', kind: 'external' },
  ],
  phases: [
    { key: 'development', label: 'Development' },
    { key: 'construction', label: 'Implementation' },
    { key: 'assetManagement', label: 'Asset Management', deferred: true },
  ],
  stages: [
    { key: 'marketing', label: 'Marketing / Lead', phase: 'development', canonicalStage: 'origination' },
    { key: 'indicativeProposal', label: 'Indicative Proposal', phase: 'development', canonicalStage: 'qualUwIc' },
    { key: 'ic1', label: 'IC1 — Investment Committee', phase: 'development', canonicalStage: 'qualUwIc', gate: true },
    { key: 'detailedProposal', label: 'Detailed Proposal & Design', phase: 'development', canonicalStage: 'designEng' },
    { key: 'agreements', label: 'Customer Agreements (PPA / ESSA)', phase: 'development', canonicalStage: 'agreements' },
    { key: 'permittingIx', label: 'Permitting & Interconnection (Rule 21)', phase: 'construction', canonicalStage: 'permittingIx' },
    { key: 'lntp', label: 'LNTP', phase: 'construction', canonicalStage: 'financingCloseNtp' },
    { key: 'soldAtNtp', label: 'Sold at NTP', phase: 'construction', canonicalStage: 'financingCloseNtp', terminal: true },
    { key: 'fntp', label: 'FNTP', phase: 'construction', canonicalStage: 'constructionProcurement' },
    { key: 'construction', label: 'Construction', phase: 'construction', canonicalStage: 'constructionProcurement' },
    { key: 'commissioningPto', label: 'Commissioning & PTO', phase: 'construction', canonicalStage: 'commissioningPtoCod' },
    { key: 'operational', label: 'Operational', phase: 'assetManagement', canonicalStage: 'operation', terminal: true },
    { key: 'cancelled', label: 'Cancelled', phase: 'assetManagement', canonicalStage: 'operation', terminal: true },
  ],
  initialStage: 'marketing',
  branches: { lntp: ['soldAtNtp'] },
  taskTemplates: [
    { stage: 'marketing', key: 'qualifyLead', label: 'Qualify CCA lead (HubSpot / Prospector); collect utility bill + interval data', role: 'Sales', milestone: 'leadQualified' },
    { stage: 'indicativeProposal', key: 'indicativeProposal', label: 'Run Energy Toolbase; produce indicative PPA/ESSA proposal', role: 'Project Development', milestone: 'indicativeProposalDelivered' },
    { stage: 'ic1', key: 'ic1', label: 'Investment Committee approval (IC1)', role: 'Investment Committee', milestone: 'ic1' },
    { stage: 'detailedProposal', key: 'detailedDesign', label: 'Detailed design, site visit, detailed build cost', role: 'Engineering', milestone: 'detailedDesignComplete' },
    { stage: 'agreements', key: 'executeAgreements', label: 'Execute PPA / ESSA + EPC system addendum', role: 'Legal', milestone: 'ppaEssaExecuted' },
    { stage: 'permittingIx', key: 'permitIx', label: 'Submit permit (AHJ) and Rule 21 interconnection', role: 'Project Development', milestone: 'fntp' },
    { stage: 'lntp', key: 'issueLntp', label: 'Reach financing close; issue LNTP', role: 'Capital Markets', milestone: 'lntp' },
    { stage: 'construction', key: 'build', label: 'Construct and commission system', role: 'EPC', milestone: 'mechanical' },
    { stage: 'commissioningPto', key: 'pto', label: 'Inspections; PTO; configure billing arrangement', role: 'EPC', milestone: 'pto' },
  ],
  docSlots: [
    { docType: 'utilityBill', label: 'Utility Bill & Interval Data (Green Button)', stage: 'indicativeProposal', requirement: 'required' },
    { docType: 'proposal', label: 'Energy Toolbase Proposal', stage: 'indicativeProposal', requirement: 'required' },
    { docType: 'financialModel', label: 'Financial Model (.xlsx)', stage: 'ic1', requirement: 'required' },
    { docType: 'ppaEssa', label: 'Executed PPA / ESSA', stage: 'agreements', requirement: 'required' },
    { docType: 'epcContract', label: 'EPC System Addendum', stage: 'agreements', requirement: 'required' },
    { docType: 'permit', label: 'Building Permit', stage: 'permittingIx', requirement: 'required' },
    { docType: 'ixAgreement', label: 'Rule 21 Interconnection Agreement', stage: 'permittingIx', requirement: 'required' },
    { docType: 'ptoLetter', label: 'PTO Letter', stage: 'commissioningPto', requirement: 'required' },
    { docType: 'completionPackage', label: 'Completion Package', stage: 'commissioningPto', requirement: 'required' },
  ],
  scheduleTiers: [
    {
      key: 'standard',
      label: 'Commercial BTM',
      milestones: [
        { key: 'leadQualified', label: 'Lead Qualified', phase: 'development', canonicalStage: 'origination', ownerRole: 'Sales', offsetDays: 7 },
        { key: 'indicativeProposalDelivered', label: 'Indicative Proposal Delivered', phase: 'development', canonicalStage: 'qualUwIc', ownerRole: 'Project Development', offsetDays: 21 },
        { key: 'ic1', label: 'IC1 Approval', phase: 'development', canonicalStage: 'qualUwIc', ownerRole: 'Investment Committee', offsetDays: 28 },
        { key: 'detailedDesignComplete', label: 'Detailed Design Complete', phase: 'development', canonicalStage: 'designEng', ownerRole: 'Engineering', offsetDays: 42 },
        { key: 'ppaEssaExecuted', label: 'PPA / ESSA Executed', phase: 'development', canonicalStage: 'agreements', ownerRole: 'Legal', offsetDays: 56 },
        { key: 'lntp', label: 'LNTP', phase: 'construction', canonicalStage: 'financingCloseNtp', ownerRole: 'Capital Markets', offsetDays: 84, isPaymentTrigger: true, requiredDocSlots: ['ppaEssa'] },
        { key: 'fntp', label: 'FNTP', phase: 'construction', canonicalStage: 'constructionProcurement', ownerRole: 'Project Development', offsetDays: 112, isPaymentTrigger: true, requiredDocSlots: ['permit'] },
        { key: 'mechanical', label: 'Mechanical Completion', phase: 'construction', canonicalStage: 'constructionProcurement', ownerRole: 'EPC', offsetDays: 154, isPaymentTrigger: true },
        { key: 'pto', label: 'PTO', phase: 'construction', canonicalStage: 'commissioningPtoCod', ownerRole: 'Utility', offsetDays: 175, requiredDocSlots: ['ptoLetter'] },
        { key: 'finalCompletion', label: 'Final Completion', phase: 'construction', canonicalStage: 'commissioningPtoCod', ownerRole: 'EPC', offsetDays: 189, isPaymentTrigger: true, requiredDocSlots: ['completionPackage'], paymentDirection: 'out' },
      ],
    },
  ],
};

// -----------------------------------------------------------------------------
// Lifecycle: V4 CCA-FTM — front-of-the-meter (utility-scale) solar + storage under CCA framework
// agreements (CEA & SDCP). Three phases; dual IC gates; WDAT (wholesale) interconnection is the long
// pole; TWO schedule tiers split at 3 MW (fast-track vs CAISO Cluster 16). One- or two-source funding;
// supports sale at NTP. Largely a variant of V2 BTM. See miscl/PMspecs3/V4_FTM.
// -----------------------------------------------------------------------------
const ceaFtmV1: ProjectManagementLifecycle = {
  key: 'cea-ftm',
  version: 1,
  label: 'CCA Front-of-the-Meter Solar + Storage',
  idPrefix: 'FTM',
  channel: 'cea-ftm',
  saleAtNtp: true,
  billingConfigurable: true,
  changeOrderApprovalPct: 3,
  roles: [
    { key: 'Business Development', label: 'Business Development', kind: 'internal' },
    { key: 'Project Development', label: 'Project Development', kind: 'internal' },
    { key: 'Capital Markets', label: 'Capital Markets', kind: 'internal' },
    { key: 'Implementation', label: 'Implementation', kind: 'internal' },
    { key: 'Construction', label: 'Construction', kind: 'internal' },
    { key: 'Investment Committee', label: 'Investment Committee', kind: 'internal' },
    { key: 'EPC', label: 'EPC', kind: 'external' },
    { key: 'Utility', label: 'Utility', kind: 'external' },
  ],
  phases: [
    { key: 'development', label: 'Development' },
    { key: 'epc', label: 'EPC' },
    { key: 'operation', label: 'Operation', deferred: true },
  ],
  stages: [
    { key: 'origination', label: 'Origination (LOI)', phase: 'development', canonicalStage: 'origination' },
    { key: 'ic1', label: 'IC1 — Development Budget', phase: 'development', canonicalStage: 'qualUwIc', gate: true },
    { key: 'interconnection', label: 'Interconnection (WDAT)', phase: 'development', canonicalStage: 'permittingIx' },
    { key: 'siteEpcProposals', label: 'Site Visit & EPC Proposals', phase: 'development', canonicalStage: 'designEng' },
    { key: 'ccaContract', label: 'CCA Approval & PPA/ESSA (Site Control)', phase: 'development', canonicalStage: 'agreements' },
    { key: 'ic2', label: 'IC2 — EPC Agreement', phase: 'epc', canonicalStage: 'agreements', gate: true },
    { key: 'financingClose', label: 'Financing Close', phase: 'epc', canonicalStage: 'financingCloseNtp', gate: true },
    { key: 'lntp', label: 'LNTP', phase: 'epc', canonicalStage: 'financingCloseNtp' },
    { key: 'soldAtNtp', label: 'Sold at NTP', phase: 'epc', canonicalStage: 'financingCloseNtp', terminal: true },
    { key: 'fntp', label: 'FNTP', phase: 'epc', canonicalStage: 'constructionProcurement' },
    { key: 'construction', label: 'Construction', phase: 'epc', canonicalStage: 'constructionProcurement' },
    { key: 'substantialPtoCod', label: 'Substantial / PTO / COD', phase: 'epc', canonicalStage: 'commissioningPtoCod' },
    { key: 'finalCompletion', label: 'Final Completion', phase: 'epc', canonicalStage: 'commissioningPtoCod' },
    { key: 'operational', label: 'Operational', phase: 'operation', canonicalStage: 'operation', terminal: true },
    { key: 'cancelled', label: 'Cancelled', phase: 'operation', canonicalStage: 'operation', terminal: true },
  ],
  initialStage: 'origination',
  branches: { lntp: ['soldAtNtp'] },
  taskTemplates: [
    { stage: 'origination', key: 'loi', label: 'Screen site; execute LOI with site host', role: 'Business Development', milestone: 'loiExecuted' },
    { stage: 'ic1', key: 'ic1', label: 'IC approval — development budget', role: 'Investment Committee', milestone: 'ic1' },
    { stage: 'interconnection', key: 'ixRequest', label: 'Submit WDAT interconnection request; run study process', role: 'Project Development', milestone: 'ixApplication' },
    { stage: 'siteEpcProposals', key: 'epcProposals', label: 'Site visit; solicit 2–3 EPC bids; entitlements if required', role: 'Project Development', milestone: 'epcSelected' },
    { stage: 'ccaContract', key: 'ccaContract', label: 'CCA project approval; execute PPA/ESSA; secure site control (Option + Lease)', role: 'Project Development', milestone: 'ppaEssaExecuted' },
    { stage: 'ic2', key: 'ic2', label: 'IC approval — EPC agreement', role: 'Investment Committee', milestone: 'ic2' },
    { stage: 'financingClose', key: 'financingClose', label: 'Financing-party approval / closing; execute EPC agreement', role: 'Capital Markets', milestone: 'financingClose' },
    { stage: 'lntp', key: 'issueLntp', label: 'Issue LNTP (design, permit, IX, long-lead); milestone payment', role: 'Implementation', milestone: 'lntp' },
    { stage: 'construction', key: 'build', label: 'Construct → substantial completion', role: 'EPC', milestone: 'substantial' },
    { stage: 'substantialPtoCod', key: 'inspectionsPtoCod', label: 'Third-party inspections; PTO / COD', role: 'EPC', milestone: 'cod' },
    { stage: 'finalCompletion', key: 'finalCompletion', label: 'Punch list; financing deliverables; final payment', role: 'EPC', milestone: 'finalCompletion' },
  ],
  docSlots: [
    { docType: 'financialModel', label: 'Financial / Pricing Model (.xlsx)', stage: 'ic1', requirement: 'required' },
    { docType: 'siteControl', label: 'Site Control (Option + Lease)', stage: 'ccaContract', requirement: 'required' },
    { docType: 'ppaEssa', label: 'Executed PPA / ESSA', stage: 'ccaContract', requirement: 'required' },
    { docType: 'ixAgreement', label: 'WDAT Interconnection Agreement', stage: 'interconnection', requirement: 'required' },
    { docType: 'epcContract', label: 'Executed EPC Agreement', stage: 'ic2', requirement: 'required' },
    { docType: 'underwritingPackage', label: 'Underwriting Package', stage: 'financingClose', requirement: 'required' },
    { docType: 'permit', label: 'Permits / Entitlements', stage: 'fntp', requirement: 'conditional', condition: 'if entitlements required' },
    { docType: 'ptoLetter', label: 'PTO / COD Letter', stage: 'substantialPtoCod', requirement: 'required' },
    { docType: 'completionPackage', label: 'Completion Package (as-builts, O&M manuals)', stage: 'finalCompletion', requirement: 'required' },
  ],
  scheduleTiers: [
    {
      key: 'cluster',
      label: 'Cluster (> 3 MW, CAISO Cluster 16)',
      selector: (p) => (p.capacityKwDc || 0) >= 3000,
      milestones: [
        { key: 'loiExecuted', label: 'LOI Executed', phase: 'development', canonicalStage: 'origination', ownerRole: 'Business Development', offsetDays: 14 },
        { key: 'ic1', label: 'IC1 — Dev Budget', phase: 'development', canonicalStage: 'qualUwIc', ownerRole: 'Investment Committee', offsetDays: 30 },
        { key: 'ixApplication', label: 'IX Application Filed (WDAT)', phase: 'development', canonicalStage: 'permittingIx', ownerRole: 'Project Development', offsetDays: 90 },
        { key: 'epcSelected', label: 'EPC Selected', phase: 'development', canonicalStage: 'designEng', ownerRole: 'Project Development', offsetDays: 800 },
        { key: 'ppaEssaExecuted', label: 'PPA / ESSA Executed (Site Control)', phase: 'development', canonicalStage: 'agreements', ownerRole: 'Project Development', offsetDays: 850 },
        { key: 'ic2', label: 'IC2 — EPC Agreement', phase: 'epc', canonicalStage: 'agreements', ownerRole: 'Investment Committee', offsetDays: 900 },
        { key: 'financingClose', label: 'Financing Close', phase: 'epc', canonicalStage: 'financingCloseNtp', ownerRole: 'Capital Markets', offsetDays: 950 },
        { key: 'lntp', label: 'LNTP / NTP', phase: 'epc', canonicalStage: 'financingCloseNtp', ownerRole: 'Implementation', offsetDays: 960, isPaymentTrigger: true },
        { key: 'fntp', label: 'FNTP', phase: 'epc', canonicalStage: 'constructionProcurement', ownerRole: 'Construction', offsetDays: 990, isPaymentTrigger: true, paymentDirection: 'in' },
        { key: 'substantial', label: 'Substantial Completion', phase: 'epc', canonicalStage: 'commissioningPtoCod', ownerRole: 'EPC', offsetDays: 1070, isPaymentTrigger: true },
        { key: 'cod', label: 'PTO / COD', phase: 'epc', canonicalStage: 'commissioningPtoCod', ownerRole: 'Utility', offsetDays: 1080, requiredDocSlots: ['ptoLetter'] },
        { key: 'finalCompletion', label: 'Final Completion', phase: 'epc', canonicalStage: 'commissioningPtoCod', ownerRole: 'EPC', offsetDays: 1110, isPaymentTrigger: true, requiredDocSlots: ['completionPackage'] },
      ],
    },
    {
      key: 'fastTrack',
      label: 'Fast-Track (< 3 MW / storage, no entitlements)',
      milestones: [
        { key: 'loiExecuted', label: 'LOI Executed', phase: 'development', canonicalStage: 'origination', ownerRole: 'Business Development', offsetDays: 14 },
        { key: 'ic1', label: 'IC1 — Dev Budget', phase: 'development', canonicalStage: 'qualUwIc', ownerRole: 'Investment Committee', offsetDays: 30 },
        { key: 'ixApplication', label: 'IX Application Filed (WDAT)', phase: 'development', canonicalStage: 'permittingIx', ownerRole: 'Project Development', offsetDays: 60 },
        { key: 'epcSelected', label: 'EPC Selected', phase: 'development', canonicalStage: 'designEng', ownerRole: 'Project Development', offsetDays: 120 },
        { key: 'ppaEssaExecuted', label: 'PPA / ESSA Executed (Site Control)', phase: 'development', canonicalStage: 'agreements', ownerRole: 'Project Development', offsetDays: 135 },
        { key: 'ic2', label: 'IC2 — EPC Agreement', phase: 'epc', canonicalStage: 'agreements', ownerRole: 'Investment Committee', offsetDays: 150 },
        { key: 'financingClose', label: 'Financing Close', phase: 'epc', canonicalStage: 'financingCloseNtp', ownerRole: 'Capital Markets', offsetDays: 195 },
        { key: 'lntp', label: 'LNTP / NTP', phase: 'epc', canonicalStage: 'financingCloseNtp', ownerRole: 'Implementation', offsetDays: 210, isPaymentTrigger: true },
        { key: 'fntp', label: 'FNTP', phase: 'epc', canonicalStage: 'constructionProcurement', ownerRole: 'Construction', offsetDays: 240, isPaymentTrigger: true, paymentDirection: 'in' },
        { key: 'substantial', label: 'Substantial Completion', phase: 'epc', canonicalStage: 'commissioningPtoCod', ownerRole: 'EPC', offsetDays: 340, isPaymentTrigger: true },
        { key: 'cod', label: 'PTO / COD', phase: 'epc', canonicalStage: 'commissioningPtoCod', ownerRole: 'Utility', offsetDays: 360, requiredDocSlots: ['ptoLetter'] },
        { key: 'finalCompletion', label: 'Final Completion', phase: 'epc', canonicalStage: 'commissioningPtoCod', ownerRole: 'EPC', offsetDays: 370, isPaymentTrigger: true, requiredDocSlots: ['completionPackage'] },
      ],
    },
  ],
};

export const projectManagementLifecycles: { [key: string]: ProjectManagementLifecycle } = {
  'ric': ricV1,
  'cea-btm': ceaBtmV1,
  'cea-ftm': ceaFtmV1,
  'res-tpo': resTpoV3,
  'res-prepaid': resPrepaidV1,
};

export const projectManagementDefaultLifecycleKey = 'ric';

/** Looks up a lifecycle definition by key. */
export const getProjectManagementLifecycle = (key: string): ProjectManagementLifecycle | undefined => projectManagementLifecycles[key];

// The vertical the Project Management workspace is working, persisted per device. Every page that shows a
// BOOK of projects reads it, so the pipeline and the bulk grid always agree on which one is on screen —
// each page keeping its own idea of it is what sent Bulk Edit to RIC from a residential pipeline. An
// unknown key (a renamed vertical, a hand-edited value) falls back to the default rather than showing
// nothing at all.
const WORKSPACE_LIFECYCLE_KEY = 'pm.lifecycle';

export const projectManagementWorkspaceLifecycle = (): string => {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(WORKSPACE_LIFECYCLE_KEY) : null;
  return saved && projectManagementLifecycles[saved] ? saved : projectManagementDefaultLifecycleKey;
};

export const projectManagementSetWorkspaceLifecycle = (key: string) => {
  try { localStorage.setItem(WORKSPACE_LIFECYCLE_KEY, key); }
  catch (_) { /* private mode */ }
};

/** The vertical's money model, defaulting to RIC's — every lifecycle ran that before residential declared
 *  its own, so an undeclared `finance` keeps a vertical exactly as it was. */
export const projectManagementFinanceModel = (lifecycle?: ProjectManagementLifecycle): ProjectManagementFinanceModel =>
  lifecycle?.finance?.model || 'ric';

/** The Uses making up this vertical's Fees figure, or nothing when its fees are Luminia's own margin. */
export const projectManagementFeeUseCodes = (lifecycle?: ProjectManagementLifecycle): string[] | undefined =>
  lifecycle?.finance?.feeUseCodes;

/** What the Project Details tab leaves out for this vertical, defaulting to RIC's — nothing. */
export const projectManagementProjectDetailsHidden = (lifecycle?: ProjectManagementLifecycle): ProjectManagementLifecycleProjectDetails =>
  lifecycle?.projectDetails || {};

/** Where the vertical's Sources figure is read from, defaulting to RIC's for the same reason. */
export const projectManagementSourcesFigure = (lifecycle?: ProjectManagementLifecycle): ProjectManagementLifecycleSourcesFigure =>
  lifecycle?.finance?.sourcesFigure || 'cashLedger';

/** The vertical's Estimated PTO source, defaulting to RIC's for the same reason. */
export const projectManagementPtoEstimate = (lifecycle?: ProjectManagementLifecycle): ProjectManagementLifecyclePtoEstimate =>
  lifecycle?.ptoEstimate || { from: 'task', key: 'pto' };

/** The vertical's model slot and parser, defaulting to RIC's for the same reason. */
export const projectManagementFinancialModelOf = (lifecycle?: ProjectManagementLifecycle): ProjectManagementLifecycleFinancialModel =>
  lifecycle?.financialModel || { docType: 'financialModel', parser: 'bta' };

// A duplicated idPrefix would mint the same project id for two verticals. Fail at boot, not at the unique
// index months later.
const idPrefixes = Object.values(projectManagementLifecycles).map((l) => l.idPrefix);
if (idPrefixes.length !== new Set(idPrefixes).size) throw new Error(`projectManagementLifecycles: duplicate idPrefix in ${idPrefixes.join(', ')}`);

/** Returns the stage definition for a stage key within a lifecycle. */
/** The lifecycle stage a milestone belongs to, resolved through the task templates that map to it. */
export const projectManagementStageForMilestoneKey = (lifecycle: ProjectManagementLifecycle, milestoneKey: string): string | undefined =>
  lifecycle.taskTemplates.find((t) => t.milestone === milestoneKey)?.stage;

export const projectManagementStageDef = (lifecycle: ProjectManagementLifecycle, stageKey: string): ProjectManagementStageDef | undefined =>
  lifecycle.stages.find((s) => s.key === stageKey);

/** Returns a stage's display label, falling back to its key. */
export const projectManagementStageLabel = (lifecycle: ProjectManagementLifecycle, stageKey: string): string =>
  projectManagementStageDef(lifecycle, stageKey)?.label || stageKey;

/**
 * Picks the schedule tier for a project: the first whose selector matches, else the first selector-less
 * tier, else the first tier.
 * @param lifecycle - the lifecycle definition
 * @param project - the project (capacity drives tier selection, e.g. FTM's 3 MW split)
 * @returns the chosen schedule tier, or undefined if the lifecycle defines none
 */
export const projectManagementScheduleTier = (
  lifecycle: ProjectManagementLifecycle,
  project: { capacityKwDc?: number; storageKwh?: number },
): ProjectManagementScheduleTier | undefined => {
  const tiers = lifecycle.scheduleTiers || [];
  return tiers.find((t) => t.selector && t.selector(project)) || tiers.find((t) => !t.selector) || tiers[0];
};

/** The payment config for a milestone key (searched across the lifecycle's tiers) — direction + amount,
 *  or empty when the milestone isn't a configured payment milestone. Legacy 1:1 form (SGIP / CEA). */
export const projectManagementMilestonePayment = (
  lifecycle: ProjectManagementLifecycle | undefined,
  milestoneKey: string,
): { direction?: 'in' | 'out'; amount?: number } => {
  for (const tier of lifecycle?.scheduleTiers || []) {
    const m = tier.milestones.find((mm) => mm.key === milestoneKey);
    if (m?.paymentDirection) return { direction: m.paymentDirection, amount: m.paymentAmount };
  }
  return {};
};

/** The cash-event rows a milestone template owns, normalizing the new `payments[]` form and the legacy
 *  single `paymentDirection` (which is equivalent to one payment whose code IS the milestone key). */
export const projectManagementMilestonePayments = (
  m: ProjectManagementMilestoneTemplate,
): { code: string; direction: 'in' | 'out'; label: string; amount?: number }[] =>
  m.payments || (m.paymentDirection ? [{ code: m.key, direction: m.paymentDirection, label: m.label, amount: m.paymentAmount }] : []);
